import clsx from 'clsx';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import FormattedDateTime from '../datetime/FormattedDateTime';
import { InternalApiDocument, InternalApiUniversalDocumentEntityType } from '../../api-internal';
import { useDocumentsPaginated } from './useDocumentsPaginated';
import {
  createColumnsForObjectData,
  SalvButton,
  SalvTable,
  SalvTablePaginationType,
  Size,
} from '@salvapp/ui-components';
import './Documents.scss';
import { universalDocumentsApi } from '../../apis';
import { downloadDocument as downloadDocumentHelper } from '../document/helper';
import { DocumentUploadModal } from './DocumentUploadModal';
import Has from '../../auth/Has';
import { CAN_DELETE_DOCUMENT, CAN_READ_DOCUMENT } from '../../auth/permissions';
import { showConfirmationModal } from '../../confirmation-modal/confirmationModalReducer';
import { useDispatch } from 'react-redux';
import { PaginationState } from '@tanstack/react-table';

const PAGE_SIZE = 20;

interface DocumentsProps {
  entityType: InternalApiUniversalDocumentEntityType;
  entityId?: number;
  entityRef?: string;
  className?: string;
  onChange?: () => void;
}

export const Documents = ({ entityType, entityId, entityRef, className, onChange }: DocumentsProps) => {
  const dispatch = useDispatch();
  const [page, setPage] = useState(0);
  const [query, setQuery] = useState<string | undefined>(undefined);

  const { documentsPage, fetchDocuments, isLoading } = useDocumentsPaginated({
    entityType,
    entityId,
    entityRef,
    page,
    size: PAGE_SIZE,
    query,
  });

  const refreshDocuments = useCallback(async () => {
    await fetchDocuments();
    onChange?.();
  }, [fetchDocuments, onChange]);

  const forEntity = useMemo(() => ({ entityType, entityId, entityRef }), [entityType, entityId, entityRef]);

  const downloadDocument = useCallback(async (document: InternalApiDocument) => {
    const blob = await universalDocumentsApi.downloadUniversalDocument({
      documentId: document.id,
    });
    downloadDocumentHelper(blob.body, document.fileName);
  }, []);

  const deleteDocument = useCallback(
    (document: InternalApiDocument) => {
      dispatch(
        showConfirmationModal({
          title: 'Are you sure?',
          description: 'You will not be able to recover the file!',
          confirmationText: 'Yes, delete it!',
          cancelText: 'No, keep it',
          onConfirm: async () => {
            await universalDocumentsApi.deleteUniversalDocument({
              documentId: document.id,
            });
            await refreshDocuments();
          },
        })
      );
    },
    [dispatch, refreshDocuments]
  );

  const [downloading, setDownloading] = useState(false);

  const downloadAllDocuments = useCallback(async () => {
    setDownloading(true);
    try {
      const blob = await universalDocumentsApi.downloadAllUniversalDocuments({
        entityType: entityType,
        entityId: entityId,
        entityRef: entityRef,
      });
      const label = entityRef ?? String(entityId);
      downloadDocumentHelper(blob.body, `${entityType.toLowerCase().replace(/_/g, '-')}-${label}-documents.zip`);
    } finally {
      setDownloading(false);
    }
  }, [entityType, entityId, entityRef]);

  const setPagination = useCallback((state: PaginationState) => {
    setPage(state.pageIndex);
  }, []);

  const onSearch = useCallback((searchQuery: string) => {
    setQuery(searchQuery || undefined);
    setPage(0);
  }, []);

  const data = documentsPage?.content ?? [];

  const columnSchema = useMemo(() => {
    return createColumnsForObjectData<InternalApiDocument>([
      {
        accessorKey: 'fileName',
        header: 'File Name',
      },
      {
        accessorKey: 'user',
        header: 'Uploaded By',
        cell: (row) => (
          <span>
            {row.user.name}, <FormattedDateTime date={row.createdTime} showTime />
          </span>
        ),
      },
      {
        accessorKey: 'userDeleted',
        header: 'Actions',
        cell: (row) =>
          row.userDeleted ? (
            <span>
              {row.userDeleted.name}, <FormattedDateTime date={row.deletedTime} showTime />
            </span>
          ) : (
            <span className='universal-documents-buttons'>
              <SalvButton
                variant='muted'
                size={Size.EXTRA_SMALL}
                label='Download'
                onClick={() => downloadDocument(row)}
              />
              <Has allPermissions={[CAN_DELETE_DOCUMENT]}>
                <SalvButton
                  variant='danger-outline'
                  size={Size.EXTRA_SMALL}
                  label='Delete'
                  onClick={() => deleteDocument(row)}
                />
              </Has>
            </span>
          ),
      },
    ]);
  }, [downloadDocument, deleteDocument]);

  const total = documentsPage?.total ?? 0;
  const isEmpty = !isLoading && total === 0 && !query;

  // Track whether entity has documents independent of search filter
  const [hasAnyDocuments, setHasAnyDocuments] = useState(false);
  useEffect(() => {
    if (!query && !isLoading) {
      setHasAnyDocuments(total > 0);
    }
  }, [query, isLoading, total]);

  return (
    <Has allPermissions={[CAN_READ_DOCUMENT]}>
      <SalvTable<InternalApiDocument>
        title='Documents'
        columns={isEmpty ? [] : columnSchema}
        className={clsx('universal-documents m-b-24', className)}
        data={data}
        isLoading={isLoading}
        search={!isEmpty}
        onSearch={onSearch}
        emptyMessage='No documents attached yet'
        pagination={
          total > PAGE_SIZE
            ? {
                type: SalvTablePaginationType.PAGINATION,
                total,
                state: {
                  pageIndex: page,
                  pageSize: PAGE_SIZE,
                },
                setPagination,
              }
            : undefined
        }
        actions={
          <>
            <SalvButton
              variant='muted'
              size={Size.SMALL}
              label={downloading ? 'Downloading...' : 'Download All'}
              onClick={downloadAllDocuments}
              disabled={downloading || !hasAnyDocuments}
            />
            <DocumentUploadModal forEntity={forEntity} refreshDocuments={refreshDocuments} />
          </>
        }
      />
    </Has>
  );
};
